import { useEffect, useMemo, useState } from "react";
import { getServices, addSale, removeSale } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Package, Copy, Check, ExternalLink, MessageCircle, Mail, ChevronDown, BadgeCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CAT_LABEL = { verkoper: "Verkoper", verhuurder: "Verhuurder", koper: "Koper", huurder: "Huurder", partner: "Partner", overig: "Overig" };

const waNumber = (phone) => {
  let p = (phone || "").replace(/[^\d+]/g, "");
  if (p.startsWith("+")) p = p.slice(1);
  else if (p.startsWith("00")) p = p.slice(2);
  else if (p.startsWith("0")) p = "31" + p.slice(1);
  if (p.startsWith("310")) p = "31" + p.slice(3);
  return p;
};

const copyText = async (v) => { try { await navigator.clipboard.writeText(v); toast.success("Gekopieerd"); } catch { toast.error("Kopiëren mislukt"); } };

function shareMessage(lead, s) {
  const first = (lead.name || "").split(" ")[0];
  const lines = [`Hoi ${first},`, "", `Zoals besproken — ${s.name}${s.price ? ` (${s.price}${s.vat_status ? " " + s.vat_status : ""})` : ""}.`];
  if (s.website_pages) lines.push(`Meer info: ${s.website_pages.split(/\s+/)[0]}`);
  if (s.checkout_link) lines.push(`Direct bestellen: ${s.checkout_link}`);
  lines.push("", "Met vriendelijke groet,");
  return lines.join("\n");
}

function PackageCard({ lead, s, compare, onToggleCompare, soldCount, onSell }) {
  const [open, setOpen] = useState(false);
  const shareWa = () => {
    const num = waNumber(lead.phone);
    if (!num) return toast.error("Geen telefoonnummer bij deze lead");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(shareMessage(lead, s))}`, "_blank");
  };
  const shareMail = () => {
    if (!lead.email) return toast.error("Geen e-mailadres bij deze lead");
    const a = document.createElement("a");
    a.href = `mailto:${lead.email}?subject=${encodeURIComponent(`Flex Estate — ${s.name}`)}&body=${encodeURIComponent(shareMessage(lead, s))}`;
    document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 200);
  };
  return (
    <div data-testid={`pkg-${s.id}`} className={`rounded-xl border p-4 transition-all ${compare ? "border-[#16BFBF] bg-[#F3FCFC]" : "border-[#E2E7EC] bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-heading font-semibold text-[#16202B]">{s.name}</h4>
            {s.category && <span className="text-xs px-2 py-0.5 rounded-full bg-[#E3E9F5] text-[#001C51]">{s.category}</span>}
          </div>
          {s.description && <p className="text-sm text-[#5B6675] mt-1">{s.description}</p>}
        </div>
        {s.price && <div className="text-right shrink-0"><p className="font-heading font-bold text-[#001C51]">{s.price}</p>{s.vat_status && <p className="text-xs text-[#5B6675]">{s.vat_status}</p>}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-[#5B6675] cursor-pointer select-none">
          <input type="checkbox" data-testid={`pkg-compare-${s.id}`} checked={compare} onChange={onToggleCompare} className="accent-[#16BFBF]" /> Vergelijk
        </label>
        {s.checkout_link && <button data-testid={`pkg-copy-${s.id}`} onClick={() => copyText(s.checkout_link)} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-full bg-[#EAFBFB] text-[#0C6E6E] border border-[#D6F3F3] hover:bg-[#D6F3F3]"><Copy className="h-3.5 w-3.5" /> Kopieer bestellink</button>}
        {s.checkout_link && <a href={s.checkout_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-full border border-[#E2E7EC] text-[#001C51] hover:bg-[#EDF1F5]"><ExternalLink className="h-3.5 w-3.5" /> Info/checkout</a>}
        <button data-testid={`pkg-sell-${s.id}`} onClick={onSell} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full bg-[#001C51] text-white hover:bg-[#0A337A] transition-colors"><BadgeCheck className="h-3.5 w-3.5" /> Verkocht registreren</button>
        {soldCount > 0 && <span data-testid={`pkg-sold-${s.id}`} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#D6F3F3] text-[#0C6E6E]">Verkocht{soldCount > 1 ? ` ×${soldCount}` : ""}</span>}
        <button data-testid={`pkg-wa-${s.id}`} onClick={shareWa} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-full text-[#0C6E6E] hover:bg-[#D6F3F3]"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</button>
        <button data-testid={`pkg-mail-${s.id}`} onClick={shareMail} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-full text-[#001C51] hover:bg-[#E3E9F5]"><Mail className="h-3.5 w-3.5" /> E-mail</button>
        {(s.included || s.excluded || s.conditions || s.duration) && <button onClick={() => setOpen((o) => !o)} className="ml-auto text-xs font-medium text-[#001C51] hover:underline inline-flex items-center gap-1">{open ? "Minder" : "Details"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /></button>}
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-[#E2E7EC] grid sm:grid-cols-2 gap-3 text-sm">
          {s.included && <div><p className="text-xs uppercase tracking-wide text-[#5B6675] font-semibold mb-0.5">Inbegrepen</p><p className="whitespace-pre-wrap text-[#16202B]">{s.included}</p></div>}
          {s.excluded && <div><p className="text-xs uppercase tracking-wide text-[#5B6675] font-semibold mb-0.5">Niet inbegrepen</p><p className="whitespace-pre-wrap text-[#16202B]">{s.excluded}</p></div>}
          {s.duration && <div><p className="text-xs uppercase tracking-wide text-[#5B6675] font-semibold mb-0.5">Looptijd</p><p className="text-[#16202B]">{s.duration}</p></div>}
          {s.conditions && <div><p className="text-xs uppercase tracking-wide text-[#5B6675] font-semibold mb-0.5">Voorwaarden</p><p className="whitespace-pre-wrap text-[#16202B]">{s.conditions}</p></div>}
        </div>
      )}
    </div>
  );
}

const WISHES = [
  { id: "zelf", label: "Zelf verkopen", ans: ["zelf verkopen", "zelf doen", "zonder makelaar", "self-service", "self service", "alles zelf"], pkg: ["self", "zelf"] },
  { id: "deels", label: "Deel van het proces", ans: ["deel", "gedeeltelijk", "hulp bij", "ondersteuning bij", "begeleiding", "beetje hulp", "sparren"], pkg: ["begeleid", "guidance", "plus", "hulp", "deels", "ondersteun"] },
  { id: "volledig", label: "Volledige begeleiding", ans: ["volledig", "uit handen", "full service", "ontzorg", "alles laten doen", "makelaar"], pkg: ["full", "volledig", "compleet", "premium"] },
  { id: "keuze", label: "Hulp bij kiezen", ans: ["weet ik nog niet", "weet niet", "twijfel", "advies", "help mij kiezen", "kiezen", "nog geen idee"], pkg: [] },
];
const leadText = (lead) => [lead?.form_answers, ...(lead?.form_submissions || []).flatMap((x) => (x.answers || []).map((a) => `${a.label} ${a.value}`))].join(" ").toLowerCase();
const detectWish = (lead) => {
  const t = leadText(lead);
  if (!t.trim()) return "";
  const best = WISHES.map((w) => [w.id, w.ans.filter((k) => t.includes(k)).length]).sort((a, b) => b[1] - a[1])[0];
  return best[1] > 0 ? best[0] : "";
};
const pkgScore = (s, w, tagged) => {
  if (tagged) return (s.wish_tags || []).includes(w.id) ? 1 : 0;
  const t = `${s.name} ${s.category} ${s.routes} ${s.description}`.toLowerCase(); return w.pkg.filter((k) => t.includes(k)).length;
};
function recommend(list, wishId) {
  const tagged = list.some((s) => s.wish_tags?.length);
  const byWish = (w) => list.filter((s) => pkgScore(s, w, tagged) > 0).sort((a, b) => pkgScore(b, w, tagged) - pkgScore(a, w, tagged));
  if (wishId === "keuze") return WISHES.filter((w) => w.pkg.length).map((w) => byWish(w)[0]).filter(Boolean).slice(0, 3);
  const w = WISHES.find((x) => x.id === wishId);
  return w ? byWish(w).slice(0, 3) : [];
}

export function MatchingPackages({ lead }) {
  const detected = useMemo(() => detectWish(lead), [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [wish, setWish] = useState(detected);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setWish(detected); setShowAll(false); }, [detected, lead?.id]);
  const [services, setServices] = useState([]);
  const [route, setRoute] = useState("");
  const [compareIds, setCompareIds] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { getServices().then((d) => { setServices(d); setLoaded(true); }).catch(() => setLoaded(true)); }, []);
  const { user } = useAuth();
  const [sales, setSales] = useState(lead?.sales || []);
  useEffect(() => { setSales(lead?.sales || []); }, [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const canUndo = ["admin", "teamleider"].includes(user?.role);
  const sell = async (s) => {
    if (!lead?.id || !window.confirm(`Registreren dat ${lead.name || "deze lead"} "${s.name}" heeft gekocht?`)) return;
    try { setSales((await addSale(lead.id, s.id)).sales || []); toast.success("Verkoop geregistreerd"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Registreren mislukt"); }
  };
  const undo = async (sale) => {
    if (!window.confirm(`Verkoop "${sale.name}" ongedaan maken?`)) return;
    try { setSales((await removeSale(lead.id, sale.id)).sales || []); toast.success("Verkoop verwijderd"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Mislukt"); }
  };

  const matched = useMemo(() => {
    const cat = lead?.category || "";
    return services.filter((s) => {
      const cats = (s.lead_categories || "").split(",").map((x) => x.trim()).filter(Boolean);
      const catOk = cats.length === 0 || cats.includes(cat);
      const routeOk = !route || (s.routes || "").toLowerCase().includes(route.toLowerCase());
      return catOk && routeOk;
    });
  }, [services, lead, route]);

  const allRoutes = useMemo(() => {
    const set = new Set();
    matched.forEach((s) => (s.routes || "").split(",").forEach((r) => r.trim() && set.add(r.trim())));
    // include routes from category-matched (ignoring current route filter) so chips persist
    services.forEach((s) => {
      const cats = (s.lead_categories || "").split(",").map((x) => x.trim()).filter(Boolean);
      if (cats.length === 0 || cats.includes(lead?.category)) (s.routes || "").split(",").forEach((r) => r.trim() && set.add(r.trim()));
    });
    return [...set];
  }, [services, matched, lead]);

  const recs = useMemo(() => recommend(matched, wish), [matched, wish]);
  const toggleCompare = (id) => setCompareIds((xs) => xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]);
  const compareList = matched.filter((s) => compareIds.includes(s.id));

  return (
    <div className="rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] p-4" data-testid="matching-packages">
      <div className="flex items-center gap-2 mb-1 text-[#001C51]">
        <Package className="h-4 w-4 text-[#16BFBF]" />
        <span className="text-sm font-semibold">Passend pakket voor {CAT_LABEL[lead?.category] || "deze lead"}</span>
      </div>
      <p className="text-xs text-[#5B6675] mb-3" data-testid="pkg-wish-info">{detected ? `Voorstel op basis van de Seller Route Finder: ${WISHES.find((w) => w.id === detected).label.toLowerCase()}.` : "Geen duidelijke wens in de formulierantwoorden — kies wat de klant wil."}</p>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="pkg-wishes">
        {WISHES.map((w) => (
          <button key={w.id} data-testid={`pkg-wish-${w.id}`} onClick={() => { setWish(w.id); setShowAll(false); }}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${wish === w.id ? "bg-[#001C51] text-white border-[#001C51]" : "bg-white text-[#5B6675] border-[#E2E7EC] hover:border-[#001C51]"}`}>
            {w.label}{w.id === detected ? " ★" : ""}
          </button>
        ))}
      </div>

      {sales.length > 0 && (
        <div className="mb-3 rounded-xl border border-[#16BFBF] bg-white p-3" data-testid="lead-sales">
          <p className="text-xs uppercase tracking-wide font-semibold text-[#0C6E6E] mb-1.5">Verkocht aan deze lead (klant)</p>
          {sales.map((x) => (
            <div key={x.id} data-testid={`sale-${x.id}`} className="flex items-center gap-2 text-sm py-1">
              <BadgeCheck className="h-4 w-4 text-[#16BFBF]" />
              <span className="font-medium text-[#16202B]">{x.name}</span>
              {x.price && <span className="text-[#5B6675]">{x.price}</span>}
              <span className="text-xs text-[#8994A3]">{new Date(x.sold_at).toLocaleDateString("nl-NL")} · {x.by_name || x.by}</span>
              {canUndo && <button data-testid={`sale-remove-${x.id}`} onClick={() => undo(x)} className="ml-auto p-1 rounded-lg text-[#B0413E] hover:bg-[#F3DEDD]" aria-label="Verkoop verwijderen"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
        </div>
      )}

      {!showAll && loaded && wish && (
        <div className="space-y-3" data-testid="pkg-recommendations">
          {recs.length === 0 ? <p className="text-sm text-[#5B6675]">Geen pakket gevonden dat bij deze wens past.</p>
            : recs.map((s) => <PackageCard key={s.id} lead={lead} s={s} compare={compareIds.includes(s.id)} onToggleCompare={() => toggleCompare(s.id)}
                soldCount={sales.filter((x) => x.service_id === s.id).length} onSell={() => sell(s)} />)}
        </div>
      )}
      {matched.length > 0 && (
        <button data-testid="pkg-show-all" onClick={() => setShowAll((v) => !v)} className="mt-3 text-xs font-medium text-[#001C51] hover:underline inline-flex items-center gap-1">
          {showAll ? "Alleen voorstel tonen" : `Alle pakketten tonen (${matched.length})`} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAll ? "rotate-180" : ""}`} />
        </button>
      )}
      {showAll && <div className="mt-3">
      {allRoutes.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={() => setRoute("")} className={`text-xs font-medium px-3 py-1.5 rounded-full border ${!route ? "bg-[#001C51] text-white border-[#001C51]" : "bg-white text-[#5B6675] border-[#E2E7EC]"}`}>Alle routes</button>
          {allRoutes.map((r) => (
            <button key={r} data-testid={`route-${r}`} onClick={() => setRoute(r)} className={`text-xs font-medium px-3 py-1.5 rounded-full border ${route === r ? "bg-[#001C51] text-white border-[#001C51]" : "bg-white text-[#5B6675] border-[#E2E7EC] hover:border-[#001C51]"}`}>{r}</button>
          ))}
        </div>
      )}

      {!loaded ? <p className="text-sm text-[#5B6675] py-2">Laden…</p>
        : matched.length === 0 ? (
          <p className="text-sm text-[#5B6675] py-2" data-testid="no-packages">
            Geen gepubliceerde pakketten voor dit lead-type. Voeg ze toe onder Beheer → Diensten en stel het lead-type in.
          </p>
        ) : (
          <div className="space-y-3">
            {matched.map((s) => (
              <PackageCard key={s.id} lead={lead} s={s} compare={compareIds.includes(s.id)} onToggleCompare={() => toggleCompare(s.id)}
                soldCount={sales.filter((x) => x.service_id === s.id).length} onSell={() => sell(s)} />
            ))}
          </div>
        )}
      </div>}

      {compareList.length >= 2 && (
        <div className="mt-4 rounded-xl border border-[#16BFBF] bg-white overflow-x-auto fe-scroll" data-testid="compare-table">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#EAFBFB] text-[#0C6E6E]">
              <th className="text-left p-2 font-semibold">Vergelijk</th>
              {compareList.map((s) => <th key={s.id} className="text-left p-2 font-semibold">{s.name}</th>)}
            </tr></thead>
            <tbody>
              {[["price", "Prijs"], ["vat_status", "Btw"], ["duration", "Looptijd"], ["included", "Inbegrepen"], ["excluded", "Niet inbegrepen"], ["conditions", "Voorwaarden"]].map(([k, label]) => (
                <tr key={k} className="border-t border-[#E2E7EC] align-top">
                  <td className="p-2 text-[#5B6675] font-medium whitespace-nowrap">{label}</td>
                  {compareList.map((s) => <td key={s.id} className="p-2 text-[#16202B] whitespace-pre-wrap">{s[k] || "—"}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
